// V2.1A Step C — Wizard Nuovo SAL (4 step con preview live)
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { salApi, formatEuro, formatQty, extractApiError } from './salApi';

const stepTitles = ['Dati SAL', 'Quantità', 'Riepilogo & Preview', 'Conferma'];

const SubcontractSALWizard = ({ open, onClose, subcontractId, onCreated }) => {
  const [step, setStep] = useState(0);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    reference_period_from: '',
    reference_period_to: '',
    request_date: new Date().toISOString().slice(0, 10),
    description: '',
    is_final_sal: false,
    notes: '',
  });
  const [rowQtys, setRowQtys] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm({
      reference_period_from: '',
      reference_period_to: '',
      request_date: new Date().toISOString().slice(0, 10),
      description: '',
      is_final_sal: false,
      notes: '',
    });
    setRowQtys({});
    setPreview(null);
    (async () => {
      setLoading(true);
      try {
        const r = await salApi.availability(subcontractId);
        setAvailability(r.data);
      } catch (e) {
        toast.error(extractApiError(e).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, subcontractId]);

  const itemsPayload = useMemo(() => {
    if (!availability?.items) return [];
    return availability.items
      .map((it) => ({
        subcontract_item_id: it.subcontract_item_id,
        requested_quantity: Number(rowQtys[it.subcontract_item_id] || 0),
      }))
      .filter(r => r.requested_quantity > 0);
  }, [availability, rowQtys]);

  const runPreview = async () => {
    if (!subcontractId) return;
    setLoading(true);
    try {
      const payload = {
        subcontract_id: subcontractId,
        reference_period_from: form.reference_period_from || null,
        reference_period_to: form.reference_period_to || null,
        request_date: form.request_date || null,
        description: form.description || null,
        is_final_sal: !!form.is_final_sal,
        notes: form.notes || null,
        items: availability.items.map(it => ({
          subcontract_item_id: it.subcontract_item_id,
          requested_quantity: Number(rowQtys[it.subcontract_item_id] || 0),
        })),
      };
      const r = await salApi.preview(subcontractId, payload);
      setPreview(r.data);
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === 2 && availability) runPreview();
  }, [step]);

  const goNext = () => setStep(s => Math.min(3, s + 1));
  const goBack = () => setStep(s => Math.max(0, s - 1));

  const canProceedStep0 = form.request_date && (!form.is_final_sal || availability?.can_create_final_sal);
  const canProceedStep1 = itemsPayload.length > 0;
  const canConfirm = preview && preview.valid && itemsPayload.length > 0;

  const doCreate = async () => {
    setSaving(true);
    try {
      const payload = {
        subcontract_id: subcontractId,
        reference_period_from: form.reference_period_from || null,
        reference_period_to: form.reference_period_to || null,
        request_date: form.request_date || null,
        description: form.description || null,
        is_final_sal: !!form.is_final_sal,
        notes: form.notes || null,
        items: itemsPayload,
      };
      const r = await salApi.create(subcontractId, payload);
      toast.success(`SAL ${r.data.sal_number} creato in bozza`);
      onCreated(r.data);
      onClose();
    } catch (e) {
      const info = extractApiError(e);
      toast.error(info.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sal-wizard-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo SAL · Step {step + 1}/4 — {stepTitles[step]}</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-neutral-500 py-8 text-center">Caricamento…</div>}

        {!loading && step === 0 && availability && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Periodo dal</Label>
                <Input type="date" data-testid="sal-wiz-period-from" value={form.reference_period_from} onChange={(e) => setForm({ ...form, reference_period_from: e.target.value })} />
              </div>
              <div>
                <Label>Periodo al</Label>
                <Input type="date" data-testid="sal-wiz-period-to" value={form.reference_period_to} onChange={(e) => setForm({ ...form, reference_period_to: e.target.value })} />
              </div>
              <div>
                <Label>Data richiesta *</Label>
                <Input type="date" data-testid="sal-wiz-request-date" value={form.request_date} onChange={(e) => setForm({ ...form, request_date: e.target.value })} />
              </div>
              <div className="flex items-end gap-2">
                <Checkbox
                  id="is_final_sal"
                  checked={form.is_final_sal}
                  onCheckedChange={(v) => setForm({ ...form, is_final_sal: v })}
                  disabled={!availability.can_create_final_sal}
                  data-testid="sal-wiz-is-final"
                />
                <Label htmlFor="is_final_sal" className="cursor-pointer">SAL finale</Label>
              </div>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input data-testid="sal-wiz-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Es. Lavorazioni luglio 2026" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea data-testid="sal-wiz-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {form.is_final_sal && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2" data-testid="sal-wiz-final-info">
                Il SAL finale non chiude automaticamente il Subappalto. La chiusura richiederà motivazione separata.
              </div>
            )}
            {!availability.can_create_final_sal && form.is_final_sal && (
              <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded p-2" data-testid="sal-wiz-final-blocked">
                SAL finale non consentito: esistono altri SAL aperti o un SAL finale è già emesso.
              </div>
            )}
          </div>
        )}

        {!loading && step === 1 && availability && (
          <div className="border border-neutral-200 rounded overflow-x-auto" data-testid="sal-wiz-quantities-table">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
                <tr>
                  <th className="text-left px-3 py-2">Codice</th>
                  <th className="text-left px-3 py-2">Descrizione</th>
                  <th className="text-left px-3 py-2">U.M.</th>
                  <th className="text-right px-3 py-2">Contrattuale</th>
                  <th className="text-right px-3 py-2">Già contab.</th>
                  <th className="text-right px-3 py-2">Residua</th>
                  <th className="text-right px-3 py-2">Richiesta</th>
                  <th className="text-right px-3 py-2">Prezzo un.</th>
                  <th className="text-right px-3 py-2">Importo</th>
                </tr>
              </thead>
              <tbody>
                {availability.items.map(it => {
                  const req = Number(rowQtys[it.subcontract_item_id] || 0);
                  const amount = req * Number(it.unit_price || 0);
                  const excess = req > Number(it.residual_quantity || 0);
                  const complete = Number(it.residual_quantity || 0) === 0;
                  return (
                    <tr key={it.subcontract_item_id} className={`border-t border-neutral-100 ${excess ? 'bg-rose-50' : complete ? 'bg-emerald-50/40' : ''}`}>
                      <td className="px-3 py-2 font-medium">{it.code || '—'}</td>
                      <td className="px-3 py-2 max-w-[280px] truncate">{it.description}</td>
                      <td className="px-3 py-2">{it.unit_measure}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatQty(it.contractual_quantity)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{formatQty(it.previously_accounted_quantity)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatQty(it.residual_quantity)}</td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          step="0.001"
                          min="0"
                          max={it.residual_quantity}
                          value={rowQtys[it.subcontract_item_id] || ''}
                          onChange={(e) => setRowQtys({ ...rowQtys, [it.subcontract_item_id]: e.target.value })}
                          className={`w-24 text-right ${excess ? 'border-rose-500' : ''}`}
                          data-testid={`sal-wiz-qty-${it.code || it.subcontract_item_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{formatEuro(it.unit_price)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEuro(amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && step === 2 && (
          <div className="space-y-3" data-testid="sal-wiz-preview-block">
            {!preview && <div className="text-neutral-500 text-sm">Calcolo preview…</div>}
            {preview && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Kpi label="Richiesto"  value={formatEuro(preview.gross_requested_amount)} testid="sal-preview-gross-req" />
                  <Kpi label="Simulato approvato" value={formatEuro(preview.gross_approved_simulated)} testid="sal-preview-gross-appr" />
                  <Kpi label="Ritenuta"   value={formatEuro(preview.retention_amount)}      tone="text-amber-700" testid="sal-preview-retention" />
                  <Kpi label="Netto autor."  value={formatEuro(preview.authorized_net_amount)}  tone="text-teal-700"  testid="sal-preview-net" />
                  <Kpi label="Precedente cum." value={formatEuro(preview.previously_accounted_total)} testid="sal-preview-prev" />
                  <Kpi label="Residuo contratto" value={formatEuro(preview.residual_contract_amount)} tone="text-neutral-700" testid="sal-preview-residual" />
                  <Kpi label="Avanzamento" value={`${(preview.progress_percent || 0).toFixed(1)}%`} testid="sal-preview-progress" />
                  <Kpi label="Finale eligibile" value={preview.final_sal_eligible ? 'Sì' : 'No'} testid="sal-preview-final-eligible" />
                </div>
                {preview.violations?.length > 0 && (
                  <div className="border border-rose-300 bg-rose-50 rounded p-3 space-y-1" data-testid="sal-preview-violations">
                    <div className="text-sm font-semibold text-rose-800">Violazioni ({preview.violations.length})</div>
                    {preview.violations.map((v, i) => (
                      <div key={i} className="text-xs text-rose-700">• {v.message}</div>
                    ))}
                  </div>
                )}
                {preview.warnings?.length > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded p-3 space-y-1" data-testid="sal-preview-warnings">
                    <div className="text-sm font-semibold text-amber-800">Avvisi</div>
                    {preview.warnings.map((w, i) => (
                      <div key={i} className="text-xs text-amber-800">• {w.message}</div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!loading && step === 3 && preview && (
          <div className="space-y-3" data-testid="sal-wiz-confirm-block">
            <div className="text-sm">
              <b>Periodo:</b> {form.reference_period_from || '—'} → {form.reference_period_to || '—'}<br />
              <b>Descrizione:</b> {form.description || '—'}<br />
              <b>Righe:</b> {itemsPayload.length}<br />
              <b>Importo richiesto:</b> {formatEuro(preview.gross_requested_amount)}<br />
              <b>Ritenuta:</b> {formatEuro(preview.retention_amount)}<br />
              <b>Netto:</b> {formatEuro(preview.authorized_net_amount)}<br />
              <b>SAL finale:</b> {form.is_final_sal ? 'Sì' : 'No'}
            </div>
            <div className="text-xs text-neutral-500">Il SAL verrà creato in <b>Bozza</b>. Nessuna presentazione automatica.</div>
          </div>
        )}

        <DialogFooter className="mt-4 flex justify-between gap-2">
          <Button variant="outline" onClick={onClose} data-testid="sal-wiz-cancel">Annulla</Button>
          <div className="flex gap-2">
            {step > 0 && <Button variant="outline" onClick={goBack} data-testid="sal-wiz-back">Indietro</Button>}
            {step < 3 && (
              <Button
                onClick={goNext}
                disabled={(step === 0 && !canProceedStep0) || (step === 1 && !canProceedStep1) || (step === 2 && preview && !preview.valid)}
                data-testid="sal-wiz-next"
              >
                Avanti
              </Button>
            )}
            {step === 3 && (
              <Button onClick={doCreate} disabled={!canConfirm || saving} data-testid="sal-wiz-create">
                {saving ? 'Creazione…' : 'Crea bozza SAL'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Kpi = ({ label, value, tone = 'text-neutral-900', testid }) => (
  <div className="border border-neutral-200 rounded p-2 flex flex-col gap-0.5" data-testid={testid}>
    <span className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</span>
    <span className={`text-sm font-semibold ${tone}`}>{value}</span>
  </div>
);

export default SubcontractSALWizard;
